import { ReactNode, useRef, useState } from "react";
import "./style.scss";

interface FeaturedVideoOffer {
  media?: {
    featured?: {
      featuredVideoURL?: string;
      featuredVideoUrl?: string;
    } | null;
  } | null;
}

interface Props {
  showVideo: boolean;
  videoUrl?: string;
  objectFit?: "cover" | "contain";
  children: ReactNode;
}

export const getFeaturedOfferVideoUrl = (
  offer?: FeaturedVideoOffer | null
): string => {
  const featured = offer?.media?.featured;
  const url = featured?.featuredVideoURL || featured?.featuredVideoUrl || "";
  return typeof url === "string" ? url.trim() : "";
};

const blockOfferActivation = (event: React.SyntheticEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

const PlayIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <path d="M3.2 1.6v10.8L12.2 7 3.2 1.6z" fill="currentColor" />
  </svg>
);

const StopIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <rect x="2.5" y="2.5" width="9" height="9" rx="1" fill="currentColor" />
  </svg>
);

const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
    <path
      d="M2.2 2.2l7.6 7.6M9.8 2.2l-7.6 7.6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const getYouTubeId = (rawUrl: string): string | null => {
  const withProtocol = /^https?:\/\//i.test(rawUrl)
    ? rawUrl
    : `https://${rawUrl}`;

  try {
    const parsed = new URL(withProtocol);
    const host = parsed.hostname.replace(/^www\./, "").replace(/^m\./, "");

    if (host === "youtu.be") {
      return parsed.pathname.split("/").filter(Boolean)[0] || null;
    }

    if (host !== "youtube.com" && host !== "youtube-nocookie.com") {
      return null;
    }

    const watchId = parsed.searchParams.get("v");
    if (watchId) return watchId;

    const parts = parsed.pathname.split("/").filter(Boolean);
    const markerIndex = parts.findIndex((part) =>
      ["embed", "shorts", "live", "v"].includes(part)
    );
    if (markerIndex >= 0 && parts[markerIndex + 1]) {
      return parts[markerIndex + 1];
    }
  } catch {
    return null;
  }

  return null;
};

const youtubeEmbedSrc = (videoId: string, autoplay: boolean) => {
  const params = new URLSearchParams({
    enablejsapi: "1",
    rel: "0",
    playsinline: "1",
    modestbranding: "1",
    controls: "1",
  });

  if (typeof window !== "undefined" && window.location.origin) {
    params.set("origin", window.location.origin);
  }

  if (autoplay) {
    params.set("autoplay", "1");
  }

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

const commandYouTube = (
  iframe: HTMLIFrameElement | null,
  func: "playVideo" | "pauseVideo" | "seekTo",
  args: unknown[] | "" = ""
) => {
  iframe?.contentWindow?.postMessage(
    JSON.stringify({ event: "command", func, args }),
    "https://www.youtube.com"
  );
};

const FeaturedOfferVideo = ({
  showVideo,
  videoUrl,
  objectFit = "cover",
  children,
}: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const youtubeRef = useRef<HTMLIFrameElement>(null);
  const youtubeStartedRef = useRef(false);
  const playingRef = useRef(false);
  const lastActionRef = useRef(0);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const draggedRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [youtubeAutoplay, setYoutubeAutoplay] = useState(false);
  const url = videoUrl?.trim() || "";
  const youtubeId = url ? getYouTubeId(url) : null;
  const enabled = showVideo && url.length > 0;

  const setPlayback = (nextPlaying: boolean) => {
    playingRef.current = nextPlaying;
    setPlaying(nextPlaying);
  };

  if (!enabled) {
    return <>{children}</>;
  }

  const resetFileVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    try {
      video.currentTime = 0;
    } catch {
      // Ignore until the video metadata is ready.
    }
  };

  const playFileVideo = () => {
    const video = videoRef.current;
    if (!video) return;

    setPlayback(true);
    const playPromise = video.play();
    if (!playPromise) return;
    playPromise.catch(() => {
      resetFileVideo();
      setPlayback(false);
    });
  };

  const playYouTube = () => {
    setPlayback(true);
    if (!youtubeStartedRef.current) {
      youtubeStartedRef.current = true;
      const nextSrc = youtubeEmbedSrc(youtubeId || "", true);
      if (youtubeRef.current) {
        youtubeRef.current.src = nextSrc;
      }
      setYoutubeAutoplay(true);
      return;
    }
    commandYouTube(youtubeRef.current, "playVideo");
  };

  const stopFileVideo = () => {
    resetFileVideo();
    setPlayback(false);
  };

  const closeYouTube = () => {
    if (youtubeId && youtubeRef.current) {
      youtubeRef.current.src = youtubeEmbedSrc(youtubeId, false);
    }
    youtubeStartedRef.current = false;
    setYoutubeAutoplay(false);
    setPlayback(false);
  };

  const isYouTubePlayerTarget = (event: React.SyntheticEvent) => {
    const target = event.target as HTMLElement | null;
    return !!target?.closest?.(".featured-offer-video__player--youtube");
  };

  const rememberPointerStart = (event: React.PointerEvent) => {
    pointerStartRef.current = { x: event.clientX, y: event.clientY };
    draggedRef.current = false;
  };

  const rememberPointerEnd = (event: React.PointerEvent) => {
    if (!pointerStartRef.current) return;
    const dx = Math.abs(event.clientX - pointerStartRef.current.x);
    const dy = Math.abs(event.clientY - pointerStartRef.current.y);
    if (dx > 8 || dy > 8) {
      draggedRef.current = true;
    }
  };

  const blockOfferClick = (event: React.SyntheticEvent) => {
    if (playingRef.current && isYouTubePlayerTarget(event)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const runVideoAction = (event: React.SyntheticEvent) => {
    if (playingRef.current && isYouTubePlayerTarget(event)) return;

    const target = event.target as HTMLElement | null;
    if (target?.closest(".swiper-pagination")) return;

    blockOfferActivation(event);

    const now = Date.now();
    if (now - lastActionRef.current < 400) return;
    lastActionRef.current = now;

    if (target?.closest("[data-video-action='stop']")) {
      stopFileVideo();
      return;
    }
    if (target?.closest("[data-video-action='close']")) {
      closeYouTube();
      return;
    }
    if (draggedRef.current) return;
    if (playingRef.current) return;

    if (youtubeId) {
      playYouTube();
      return;
    }
    playFileVideo();
  };

  return (
    <div
      className={`featured-offer-video${
        playing ? " featured-offer-video--playing" : ""
      }`}
      onPointerDownCapture={rememberPointerStart}
      onPointerUpCapture={rememberPointerEnd}
      onClickCapture={runVideoAction}
      onClick={blockOfferClick}
    >
      <div
        className={`featured-offer-video__preview${
          playing ? " featured-offer-video__preview--hidden" : ""
        }`}
      >
        {children}
      </div>

      {!youtubeId && (
        <video
          ref={videoRef}
          className={`featured-offer-video__player${
            playing ? " featured-offer-video__player--playing" : ""
          }`}
          src={url}
          playsInline
          preload="auto"
          style={{ objectFit }}
          onEnded={stopFileVideo}
        />
      )}

      {youtubeId && (
        <iframe
          ref={youtubeRef}
          className={`featured-offer-video__player featured-offer-video__player--youtube${
            playing ? " featured-offer-video__player--playing" : ""
          }`}
          src={youtubeEmbedSrc(youtubeId, youtubeAutoplay)}
          title="Featured offer video"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
        />
      )}

      {playing && !youtubeId && <div className="featured-offer-video__shield" />}

      {!playing && (
        <div className="featured-offer-video__controls">
          <button
            type="button"
            className="featured-offer-video__control featured-offer-video__control--play"
            aria-label="Play video"
            tabIndex={-1}
          >
            <PlayIcon />
          </button>
        </div>
      )}

      {playing && !youtubeId && (
        <div className="featured-offer-video__controls">
          <button
            type="button"
            className="featured-offer-video__control featured-offer-video__control--stop"
            data-video-action="stop"
            aria-label="Stop video"
            tabIndex={-1}
          >
            <StopIcon />
          </button>
        </div>
      )}

      {playing && youtubeId && (
        <button
          type="button"
          className="featured-offer-video__close"
          data-video-action="close"
          aria-label="Close video"
          tabIndex={-1}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
};

export default FeaturedOfferVideo;
